import { useEffect, useRef, useState } from "react";
import { BiChevronDown } from "react-icons/bi";

import { Checkbox } from "components/ui/Checkbox/Checkbox";

import s from "./MultiSelect.module.css";

// Controlled multi-select matching the look of the single `Select` primitive.
// `value` is an array of option values; `onChange` receives the next array.
export const MultiSelect = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  disabled = false,
  error = false,
}) => {
  const selectedValues = value || [];
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const containerRef = useRef(null);

  // Max-height of the dropdown (keep in sync with .dropdown max-height in CSS).
  const DROPDOWN_MAX_HEIGHT = 240;

  const handleToggle = (optionValue) => {
    const next = selectedValues.includes(optionValue)
      ? selectedValues.filter((selected) => selected !== optionValue)
      : [...selectedValues, optionValue];
    onChange(next);
  };

  // Flip the menu above the trigger when there isn't enough room below it, so
  // the options never extend past the bottom of the viewport.
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setOpenUp(spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label);

  const displayText = selectedLabels.join(", ");

  return (
    <div className={s.container} ref={containerRef}>
      <button
        type="button"
        className={`${s.trigger} ${error ? s.triggerError : ""}`}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        disabled={disabled}
      >
        <span className={displayText ? s.value : s.placeholder}>
          {displayText || placeholder}
        </span>
        <BiChevronDown className={s.icon} />
      </button>
      {!!isOpen && (
        <div className={`${s.dropdown} ${openUp ? s.dropdownUp : ""}`}>
          {options.map((option) => (
            <div
              key={option.value}
              className={s.option}
              data-value={option.value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleToggle(option.value)}
            >
              <Checkbox
                checked={selectedValues.includes(option.value)}
                onChange={() => {}}
              />
              <span className={s.optionLabel}>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
